import { useState } from "react";
import { Apple, Mail, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import { isPrelaunch } from "@/lib/launchMode";

type EmailView = "login" | "forgot";

export type ConsumerAuthFormProps = {
  /** Called after successful sign-in / sign-up. */
  onSuccess?: () => void;
  /** Layout: modal sheet vs full page card. */
  variant?: "modal" | "page";
};

export function ConsumerAuthForm({ onSuccess, variant = "page" }: ConsumerAuthFormProps) {
  const { t } = useTranslation("consumer");
  const prelaunch = isPrelaunch();
  const {
    signInWithApple,
    signInWithGoogle,
    signInWithPassword,
    signUpWithPassword,
    resetPasswordForEmail,
    authError,
    clearAuthError,
    skip,
  } = useAuth();
  const [showEmail, setShowEmail] = useState(prelaunch);
  const [emailView, setEmailView] = useState<EmailView>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState<
    "apple" | "google" | "continue" | "signUp" | "reset" | null
  >(null);
  const [resetSent, setResetSent] = useState(false);

  const resetEmailForm = () => {
    clearAuthError();
    setEmailView("login");
    setResetSent(false);
  };

  const runOAuth = async (provider: "apple" | "google") => {
    if (prelaunch) return;
    resetEmailForm();
    setLoading(provider);
    try {
      if (provider === "apple") await signInWithApple();
      else await signInWithGoogle();
    } finally {
      setLoading(null);
    }
  };

  const runContinue = async () => {
    resetEmailForm();
    setLoading("continue");
    try {
      const result = await signInWithPassword(email, password);
      if (result.ok) onSuccess?.();
    } finally {
      setLoading(null);
    }
  };

  const runSignUp = async () => {
    if (prelaunch) return;
    clearAuthError();
    setLoading("signUp");
    try {
      const ok = await signUpWithPassword(email, password);
      if (ok) onSuccess?.();
    } finally {
      setLoading(null);
    }
  };

  const runResetPassword = async () => {
    clearAuthError();
    setResetSent(false);
    setLoading("reset");
    try {
      const error = await resetPasswordForEmail(email);
      if (!error) setResetSent(true);
    } finally {
      setLoading(null);
    }
  };

  const shellClass =
    variant === "modal"
      ? "w-full max-w-md rounded-t-3xl border-t border-border bg-surface-elevated p-6 pb-10 shadow-card"
      : "w-full max-w-md rounded-3xl border border-border bg-surface-elevated p-6 shadow-card";

  return (
    <div className={shellClass}>
      {variant === "modal" ? (
        <div className="mx-auto mb-5 h-1 w-10 rounded-full bg-border" />
      ) : null}

      <div>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {t("login.welcomeTo")}
        </p>
        <h2 id="login-modal-title" className="font-headline mt-1 text-3xl font-bold">
          Pulse
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t("login.subtitle")}</p>
        {prelaunch ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Beta access only — sign in with an invited account.
          </p>
        ) : null}
      </div>

      {authError && (
        <p className="mt-4 rounded-2xl border border-pulse/30 bg-pulse/10 px-4 py-3 text-sm text-pulse">
          {authError}
        </p>
      )}

      {resetSent && (
        <p className="mt-4 rounded-2xl border border-border bg-surface px-4 py-3 text-sm text-foreground">
          {t("login.resetPasswordSent")}
        </p>
      )}

      {!showEmail ? (
        <div className="mt-7 space-y-3">
          {!prelaunch ? (
            <>
              <AuthButton
                loading={loading === "apple"}
                onClick={() => void runOAuth("apple")}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-foreground py-3.5 text-sm font-bold text-background"
              >
                <Apple className="h-5 w-5" /> {t("login.continueWithApple")}
              </AuthButton>
              <AuthButton
                loading={loading === "google"}
                onClick={() => void runOAuth("google")}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-surface py-3.5 text-sm font-bold"
              >
                <GoogleG className="h-4 w-4" /> {t("login.continueWithGoogle")}
              </AuthButton>
            </>
          ) : null}
          <button
            type="button"
            onClick={() => {
              resetEmailForm();
              setShowEmail(true);
            }}
            className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-surface py-3.5 text-sm font-bold text-foreground/90"
          >
            <Mail className="h-4 w-4" /> {t("login.continueWithEmail")}
          </button>
          {!prelaunch ? (
            <button
              type="button"
              onClick={() => {
                skip();
                onSuccess?.();
              }}
              className="w-full py-2.5 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground underline-offset-2 hover:underline"
            >
              {t("login.continueAsGuest")}
            </button>
          ) : null}
        </div>
      ) : emailView === "forgot" ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runResetPassword();
          }}
          className="mt-7 space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("login.email")}
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-surface px-4 py-3.5 text-sm outline-none focus:border-pulse"
            />
          </label>

          <AuthButton
            loading={loading === "reset"}
            type="submit"
            className="flex w-full items-center justify-center rounded-2xl bg-gradient-pulse py-3.5 text-sm font-bold text-pulse-foreground shadow-pulse disabled:opacity-60"
          >
            {t("login.sendResetLink")}
          </AuthButton>

          <button
            type="button"
            onClick={() => {
              setEmailView("login");
              clearAuthError();
              setResetSent(false);
            }}
            className="w-full py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
          >
            {t("login.backToLogin")}
          </button>
        </form>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void runContinue();
          }}
          className="mt-7 space-y-3"
        >
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("login.email")}
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              placeholder={t("login.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-surface px-4 py-3.5 text-sm outline-none focus:border-pulse"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("login.password")}
            </span>
            <input
              type="password"
              required
              minLength={6}
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-border bg-surface px-4 py-3.5 text-sm outline-none focus:border-pulse"
            />
          </label>

          <button
            type="button"
            onClick={() => {
              setEmailView("forgot");
              clearAuthError();
              setResetSent(false);
            }}
            className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
          >
            {t("login.forgotPassword")}
          </button>

          <AuthButton
            loading={loading === "continue"}
            disabled={loading === "signUp"}
            type="submit"
            className="flex w-full items-center justify-center rounded-2xl bg-gradient-pulse py-3.5 text-sm font-bold text-pulse-foreground shadow-pulse disabled:opacity-60"
          >
            {t("login.signIn")}
          </AuthButton>

          {!prelaunch ? (
            <AuthButton
              loading={loading === "signUp"}
              disabled={loading === "continue"}
              type="button"
              onClick={() => void runSignUp()}
              className="flex w-full items-center justify-center rounded-2xl border border-border bg-surface py-3.5 text-sm font-bold text-foreground disabled:opacity-60"
            >
              {t("login.createAccount")}
            </AuthButton>
          ) : null}

          {!prelaunch ? (
            <button
              type="button"
              onClick={() => {
                setShowEmail(false);
                resetEmailForm();
              }}
              className="w-full py-2 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
            >
              {t("login.otherOptions")}
            </button>
          ) : null}
        </form>
      )}

      <p className="mt-6 text-center text-[10px] uppercase tracking-wider text-muted-foreground">
        {t("login.terms")}
      </p>
    </div>
  );
}

function AuthButton({
  loading,
  disabled,
  children,
  className,
  onClick,
  type = "button",
}: {
  loading: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className: string;
  onClick?: () => void;
  type?: "button" | "submit";
}) {
  return (
    <button type={type} disabled={loading || disabled} onClick={onClick} className={className}>
      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : children}
    </button>
  );
}

function GoogleG({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.3 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5c10.7 0 19.5-7.7 19.5-19.5 0-1.2-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.7 2.9l5.7-5.7C33.7 6.3 29.1 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 43.5c5 0 9.6-1.7 13.1-4.7l-6-5.1c-2 1.4-4.4 2.3-7.1 2.3-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39 16.3 43.5 24 43.5z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.7l6 5.1c-.4.4 6.4-4.7 6.4-14.8 0-1.2-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
